import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Share2, Brain, MessageCircle, Loader2, AlertCircle, ExternalLink, ArrowLeft, PenLine, Settings, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import QAComposerPanel from "@/components/QAComposerPanel";
import { API } from "@/lib/api";
import axios from "axios";

/**
 * AdminInsights
 *
 * Admin-only dashboard rolling up share-click telemetry from db.share_clicks.
 * Renders three blocks:
 *   1. Total + window selector
 *   2. Top 10 shared Q&As with per-channel breakdown
 *   3. Channel split with simple bar chart
 *
 * Tied to `require_admin` on the backend so a non-admin who guesses the URL
 * gets a 403 + a friendly empty state.
 */
const WINDOW_OPTIONS = [7, 30, 90];
const CHANNEL_COLOURS = {
  facebook: "#1877F2",
  x: "#0F1419",
  whatsapp: "#25D366",
  copy: "#1B6C62",
  other: "#9CA3AF",
};

export default function AdminInsights() {
  const token = typeof window !== "undefined" ? localStorage.getItem("tt_token") : null;
  const [tab, setTab] = useState("shares");
  const [days, setDays] = useState(30);

  return (
    <div className="min-h-screen paper-grain" data-testid="admin-insights-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 sm:px-8 py-12">

        <Link to="/dashboard" className="text-xs text-[#1B6C62] hover:underline inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={12} strokeWidth={1.75} /> Back to dashboard
        </Link>

        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="label-mono mb-1 text-[#1B6C62]">Admin · Insights</div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] mb-2">
              {tab === "shares" ? "Share-click leaderboard" : tab === "brain" ? "Brain usage" : tab === "settings" ? "Admin settings" : "Q&A composer"}
            </h1>
            <p className="text-sm text-[#6B7280] max-w-xl font-serif">
              {tab === "shares"
                ? "Which Q&A pages are renters spreading and through which channels. Use this to decide what to write more of."
                : tab === "brain"
                ? "What renters are asking the Brain. Spot recurring needs to prioritise new Q&A content and feature work."
                : tab === "settings"
                ? "Tunable app-wide settings. Changes apply immediately to new signups. Use carefully."
                : "Author and publish new Q&A entries for the public /q library. Ask Compass to draft an answer, review and edit, then publish."}
            </p>
          </div>
          <div className="inline-flex border border-[#E5E4DE]" data-testid="window-toggle">
            {WINDOW_OPTIONS.map((d, i) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                disabled={tab === "compose"}
                className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] ${i > 0 ? "border-l border-[#E5E4DE]" : ""} ${days === d ? "bg-[#1B6C62] text-white" : "bg-white text-[#6B7280] hover:text-[#0D2B52]"} disabled:opacity-30 disabled:cursor-not-allowed`}
                data-testid={`window-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Tab strip */}
        <div className="flex gap-1 mb-6 border-b border-[#E5E4DE]" data-testid="insights-tabs">
          <button
            type="button"
            onClick={() => setTab("shares")}
            className={`px-3 py-2 font-mono text-[11px] tracking-[0.15em] uppercase inline-flex items-center gap-2 ${tab === "shares" ? "text-[#0D2B52] border-b-2 border-[#1B6C62] -mb-px" : "text-[#9CA3AF] hover:text-[#0D2B52]"}`}
            data-testid="tab-shares"
          >
            <Share2 size={12} strokeWidth={1.75} /> Shares
          </button>
          <button
            type="button"
            onClick={() => setTab("brain")}
            className={`px-3 py-2 font-mono text-[11px] tracking-[0.15em] uppercase inline-flex items-center gap-2 ${tab === "brain" ? "text-[#0D2B52] border-b-2 border-[#1B6C62] -mb-px" : "text-[#9CA3AF] hover:text-[#0D2B52]"}`}
            data-testid="tab-brain"
          >
            <Brain size={12} strokeWidth={1.75} /> Brain usage
          </button>
          <button
            type="button"
            onClick={() => setTab("compose")}
            className={`px-3 py-2 font-mono text-[11px] tracking-[0.15em] uppercase inline-flex items-center gap-2 ${tab === "compose" ? "text-[#0D2B52] border-b-2 border-[#1B6C62] -mb-px" : "text-[#9CA3AF] hover:text-[#0D2B52]"}`}
            data-testid="tab-compose"
          >
            <PenLine size={12} strokeWidth={1.75} /> Q&amp;A composer
          </button>
          <button
            type="button"
            onClick={() => setTab("settings")}
            className={`px-3 py-2 font-mono text-[11px] tracking-[0.15em] uppercase inline-flex items-center gap-2 ${tab === "settings" ? "text-[#0D2B52] border-b-2 border-[#1B6C62] -mb-px" : "text-[#9CA3AF] hover:text-[#0D2B52]"}`}
            data-testid="tab-settings"
          >
            <Settings size={12} strokeWidth={1.75} /> Settings
          </button>
        </div>

        {tab === "shares" && <SharesPanel days={days} token={token} />}
        {tab === "brain" && <BrainPanel days={days} token={token} />}
        {tab === "compose" && <QAComposerPanel token={token} />}
        {tab === "settings" && <SettingsPanel token={token} />}

      </div>
    </div>
  );
}

/* ---------- Shared loading / error fragments ---------- */

function LoadingCard() {
  return (
    <div className="card text-center py-16" data-testid="insights-loading">
      <Loader2 size={20} className="animate-spin text-[#1B6C62] mx-auto mb-3" />
      <div className="text-sm text-[#6B7280]">Crunching the numbers…</div>
    </div>
  );
}

function ErrorCard({ message }) {
  return (
    <div className="card border-l-4 border-l-[#C9A961] bg-[#FAF6E8]" data-testid="insights-error">
      <div className="flex items-start gap-3">
        <AlertCircle size={18} className="text-[#8A6D3B] mt-0.5" strokeWidth={1.75} />
        <div>
          <div className="font-serif text-base text-[#0D2B52] mb-1">Couldn&rsquo;t load insights</div>
          <div className="text-sm text-[#6B7280]">{message}</div>
        </div>
      </div>
    </div>
  );
}

function useInsightsFetch(endpoint, days, token) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setData(null);
    axios
      .get(`${API}/admin/insights/${endpoint}?days=${days}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(({ data }) => alive && setData(data))
      .catch((e) => alive && setError(e.response?.status === 403 ? "Admin only." : (e.response?.data?.detail || "Couldn't load insights.")))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [endpoint, days, token]);
  return { data, loading, error };
}

/* ---------- Shares panel ---------- */

function SharesPanel({ days, token }) {
  const { data, loading, error } = useInsightsFetch("shares", days, token);
  if (loading) return <LoadingCard />;
  if (error) return <ErrorCard message={error} />;
  if (!data) return null;

  return (
    <div className="space-y-6">

      {/* Headline number */}
      <div className="card flex items-center gap-4">
        <div className="p-3 bg-[#E6F5F2] text-[#1B6C62]">
          <Share2 size={20} strokeWidth={1.75} />
        </div>
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#6B7280]">
            Total share clicks · last {data.window_days} days
          </div>
          <div className="font-serif text-4xl text-[#0D2B52]" data-testid="insights-total">
            {data.total.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Top 10 */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
          <h2 className="font-serif text-xl text-[#0D2B52]">Top 10 shared Q&amp;As</h2>
        </div>
        {data.top_qa.length === 0 ? (
          <div className="text-sm text-[#6B7280] italic">No shares recorded yet for this window.</div>
        ) : (
          <ol className="space-y-3" data-testid="insights-top-qa">
            {data.top_qa.map((q, i) => (
              <li key={q.slug} className="flex items-start gap-4 pb-3 border-b border-[#E5E4DE] last:border-b-0" data-testid={`insights-row-${i}`}>
                <div className="font-mono text-[18px] text-[#9CA3AF] w-7 shrink-0 mt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/q/${q.slug}`}
                    target="_blank"
                    className="font-serif text-[15px] text-[#0D2B52] hover:text-[#1B6C62] inline-flex items-center gap-1.5"
                  >
                    <span className="truncate">{q.title}</span>
                    <ExternalLink size={11} strokeWidth={1.75} className="opacity-60 shrink-0" />
                  </Link>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {Object.entries(q.channels)
                      .sort((a, b) => b[1] - a[1])
                      .map(([ch, n]) => (
                        <span
                          key={ch}
                          className="font-mono text-[10px] px-1.5 py-0.5 border"
                          style={{
                            borderColor: (CHANNEL_COLOURS[ch] || "#9CA3AF") + "55",
                            color: CHANNEL_COLOURS[ch] || "#9CA3AF",
                          }}
                        >
                          {ch} · {n}
                        </span>
                      ))}
                  </div>
                </div>
                <div className="font-mono text-xl text-[#1B6C62] shrink-0" data-testid={`insights-count-${i}`}>
                  {q.count}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Channel breakdown */}
      <div className="card">
        <h2 className="font-serif text-xl text-[#0D2B52] mb-4">Channel breakdown</h2>
        {data.by_channel.length === 0 ? (
          <div className="text-sm text-[#6B7280] italic">No channel data yet.</div>
        ) : (
          <ul className="space-y-3" data-testid="insights-channels">
            {data.by_channel.map((c) => {
              const max = data.by_channel[0]?.count || 1;
              const pct = Math.max(2, Math.round((c.count / max) * 100));
              return (
                <li key={c.channel} className="flex items-center gap-3" data-testid={`channel-${c.channel}`}>
                  <div className="w-20 font-mono text-[11px] uppercase tracking-[0.1em] text-[#0D2B52] shrink-0">
                    {c.channel}
                  </div>
                  <div className="flex-1 bg-[#FAF9F6] h-5 border border-[#E5E4DE] relative">
                    <div
                      className="absolute inset-y-0 left-0"
                      style={{ width: `${pct}%`, background: CHANNEL_COLOURS[c.channel] || "#9CA3AF" }}
                    />
                  </div>
                  <div className="w-12 text-right font-mono text-sm text-[#0D2B52] shrink-0">
                    {c.count}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {data.by_day.length > 1 && (
        <div className="card">
          <h2 className="font-serif text-xl text-[#0D2B52] mb-1">Daily activity</h2>
          <div className="text-[11px] text-[#6B7280] font-mono mb-4">
            {data.by_day[0].day} → {data.by_day[data.by_day.length - 1].day}
          </div>
          <Sparkline points={data.by_day} />
        </div>
      )}

    </div>
  );
}

/* ---------- Brain panel ---------- */

const STATE_COLOURS = {
  QLD: "#8B5A2B", NSW: "#1B6C62", VIC: "#5B3A8B",
  SA: "#A8482E", WA: "#0D6E8C", TAS: "#3A7A3A",
  NT: "#B5651D", ACT: "#3A4D8C", "?": "#9CA3AF",
};

function BrainPanel({ days, token }) {
  const { data, loading, error } = useInsightsFetch("brain", days, token);
  if (loading) return <LoadingCard />;
  if (error) return <ErrorCard message={error} />;
  if (!data) return null;

  const anonPct = data.total > 0 ? Math.round((data.anon_count / data.total) * 100) : 0;

  return (
    <div className="space-y-6">

      {/* Headline + signup conversion */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card flex items-center gap-3">
          <div className="p-2.5 bg-[#E6F5F2] text-[#1B6C62]">
            <Brain size={18} strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280]">
              Brain asks · {data.window_days}d
            </div>
            <div className="font-serif text-3xl text-[#0D2B52]" data-testid="brain-total">
              {data.total.toLocaleString()}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mb-1">Anonymous</div>
          <div className="font-serif text-2xl text-[#0D2B52]" data-testid="brain-anon">{data.anon_count.toLocaleString()}</div>
          <div className="text-[11px] text-[#9CA3AF] font-mono mt-1">{anonPct}% of total</div>
        </div>
        <div className="card">
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mb-1">Signed-in</div>
          <div className="font-serif text-2xl text-[#1B6C62]" data-testid="brain-authed">{data.authed_count.toLocaleString()}</div>
          <div className="text-[11px] text-[#9CA3AF] font-mono mt-1">{100 - anonPct}% of total</div>
        </div>
      </div>

      {/* Top questions */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
          <h2 className="font-serif text-xl text-[#0D2B52]">Top questions</h2>
          <span className="text-[11px] text-[#9CA3AF] font-mono">(grouped, case-insensitive)</span>
        </div>
        {data.top_questions.length === 0 ? (
          <div className="text-sm text-[#6B7280] italic">No questions recorded yet for this window.</div>
        ) : (
          <ol className="space-y-3" data-testid="brain-top-questions">
            {data.top_questions.map((q, i) => (
              <li key={i} className="flex items-start gap-4 pb-3 border-b border-[#E5E4DE] last:border-b-0" data-testid={`brain-row-${i}`}>
                <div className="font-mono text-[18px] text-[#9CA3AF] w-7 shrink-0 mt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-[14.5px] text-[#0D2B52] leading-snug">
                    {q.question}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(q.states || []).map((s) => (
                      <span
                        key={s}
                        className="font-mono text-[10px] px-1.5 py-0.5 border"
                        style={{
                          borderColor: (STATE_COLOURS[s] || "#9CA3AF") + "55",
                          color: STATE_COLOURS[s] || "#9CA3AF",
                        }}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="font-mono text-xl text-[#1B6C62] shrink-0" data-testid={`brain-count-${i}`}>
                  {q.count}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* State breakdown */}
      <div className="card">
        <h2 className="font-serif text-xl text-[#0D2B52] mb-4">Asks by state</h2>
        {data.by_state.length === 0 ? (
          <div className="text-sm text-[#6B7280] italic">No state data yet.</div>
        ) : (
          <ul className="space-y-3" data-testid="brain-states">
            {data.by_state.map((s) => {
              const max = data.by_state[0]?.count || 1;
              const pct = Math.max(2, Math.round((s.count / max) * 100));
              return (
                <li key={s.state} className="flex items-center gap-3" data-testid={`brain-state-${s.state}`}>
                  <div className="w-20 font-mono text-[11px] uppercase tracking-[0.1em] text-[#0D2B52] shrink-0">
                    {s.state}
                  </div>
                  <div className="flex-1 bg-[#FAF9F6] h-5 border border-[#E5E4DE] relative">
                    <div
                      className="absolute inset-y-0 left-0"
                      style={{ width: `${pct}%`, background: STATE_COLOURS[s.state] || "#9CA3AF" }}
                    />
                  </div>
                  <div className="w-12 text-right font-mono text-sm text-[#0D2B52] shrink-0">
                    {s.count}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {data.by_day.length > 1 && (
        <div className="card">
          <h2 className="font-serif text-xl text-[#0D2B52] mb-1">Daily activity</h2>
          <div className="text-[11px] text-[#6B7280] font-mono mb-4">
            {data.by_day[0].day} → {data.by_day[data.by_day.length - 1].day}
          </div>
          <Sparkline points={data.by_day} />
        </div>
      )}

    </div>
  );
}

/* ---------- Sparkline ---------- */
function Sparkline({ points }) {
  if (!points || points.length === 0) return null;
  const max = Math.max(...points.map((p) => p.count), 1);
  const W = 600;
  const H = 80;
  const stepX = points.length > 1 ? W / (points.length - 1) : 0;
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${i * stepX} ${H - (p.count / max) * H}`)
    .join(" ");
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: "80px" }} data-testid="insights-sparkline">
        <path d={path} fill="none" stroke="#1B6C62" strokeWidth="2" />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={i * stepX}
            cy={H - (p.count / max) * H}
            r="2.5"
            fill="#1B6C62"
          >
            <title>{p.day}: {p.count}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
}

/* ---------- Settings panel ---------- */

function SettingsPanel({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [s, setS] = useState({ signup_trial_enabled: false, signup_trial_tier: "copilot", signup_trial_days: 7 });

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setS((prev) => ({ ...prev, ...data })))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load settings"))
      .finally(() => setLoading(false));
  }, [token]);

  const save = async (patch) => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/admin/settings`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setS((prev) => ({ ...prev, ...data }));
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingCard />;
  if (error) return <ErrorCard message={error} />;

  return (
    <div className="space-y-4" data-testid="settings-panel">
      <div className="card">
        <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Signup trial</h3>
        <p className="text-sm text-[#6B7280] font-serif mb-4">
          When ON, every new free signup is upgraded to the chosen tier for the chosen number of days. After expiry the existing tier-expiry cron drops them back to free automatically. A given email can only claim the trial once.
        </p>
        <label className="flex items-center gap-3 mb-4 cursor-pointer" data-testid="trial-enabled-toggle">
          <input
            type="checkbox"
            checked={!!s.signup_trial_enabled}
            onChange={(e) => save({ signup_trial_enabled: e.target.checked })}
            className="w-4 h-4 accent-[#1B6C62]"
            disabled={saving}
          />
          <span className="font-mono text-[12px] uppercase tracking-[0.12em] text-[#0D2B52]">
            Signup trial {s.signup_trial_enabled ? "ON" : "OFF"}
          </span>
          {saving && <Loader2 size={13} className="animate-spin text-[#6B7280]" />}
          {savedAt && !saving && (
            <span className="font-mono text-[10px] text-[#1B6C62] inline-flex items-center gap-1">
              <Check size={11} strokeWidth={2} /> saved {savedAt}
            </span>
          )}
        </label>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-mono block mb-1.5">Trial tier</label>
            <select
              className="input-field"
              value={s.signup_trial_tier}
              onChange={(e) => save({ signup_trial_tier: e.target.value })}
              disabled={saving}
              data-testid="trial-tier-select"
            >
              <option value="vault">Vault</option>
              <option value="copilot">Compass</option>
              <option value="dispute">Dispute & Tribunal</option>
              <option value="pro">Pro</option>
            </select>
          </div>
          <div>
            <label className="label-mono block mb-1.5">Trial duration (days)</label>
            <input
              type="number"
              min="1"
              max="30"
              className="input-field font-mono"
              value={s.signup_trial_days}
              onChange={(e) => setS({ ...s, signup_trial_days: parseInt(e.target.value || "0", 10) })}
              onBlur={(e) => {
                const n = parseInt(e.target.value || "0", 10);
                if (n >= 1 && n <= 30) save({ signup_trial_days: n });
              }}
              disabled={saving}
              data-testid="trial-days-input"
            />
          </div>
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-3 italic font-serif">
          Tip: keep this OFF until you have a clear acquisition push planned. Switching it ON mid-week can spike AI spend if signups surge.
        </p>
      </div>
    </div>
  );
}

